// ============================================================
// CineSync Video Transfer Service
//
// High-throughput WebRTC DataChannel peer-to-peer streaming engine
// providing YouTube-style advance buffering in browser RAM.
//
// Key Features:
// 1. YouTube-style forward advance buffering in browser RAM.
// 2. Head + Tail chunk prioritization (moov/ftyp/ebml container headers first).
// 3. WebRTC SCTP send buffer flow control (bufferedAmountLowThreshold).
// 4. In-memory ChunkStore for sub-millisecond HTTP 206 range responses.
// 5. Zero-corruption guarantee: Never return zero-filled buffers for missing chunks;
//    instead, dynamically prioritize and await chunks over DataChannel.
// 6. Partial range fulfillment: RFC 7233 compliant partial range responses for
//    instantaneous playback start before entire ranges arrive.
// 7. Full native Blob URL creation when buffer is complete for 100% hardware-accelerated playback.
// 8. Instant priority seeking without buffer stalls.
// ============================================================

import { usePlayerStore } from '@/store/playerStore';

export const CHUNK_SIZE = 64 * 1024; // 64 KB per chunk (optimal for WebRTC SCTP packets)
export const BUFFER_THRESHOLD = 512 * 1024; // 512 KB threshold for onbufferedamountlow
export const ADVANCE_BUFFER_SECONDS = 15 * 60; // Keep 15 minutes in advance in browser RAM
export const HEAD_CHUNKS_COUNT = 32; // First 2 MB of file (container headers & moov faststart)
export const TAIL_CHUNKS_COUNT = 32; // Last 2 MB of file (moov atom if non-fragmented MP4)

export interface FileMetadata {
  type: 'FILE_META';
  fileName: string;
  fileSize: number;
  fileType: string;
  duration: number;
  chunkSize: number;
  totalChunks: number;
  streamId: string;
}

export type StreamControlMessage =
  | FileMetadata
  | { type: 'SEEK_PRIORITY'; targetChunk: number; targetTime: number }
  | { type: 'CHUNK_REQUEST'; startChunk: number; count: number }
  | { type: 'BUFFER_ACK'; bufferedPercent: number; bufferHealthSeconds: number };

// ── Host Video Sender ─────────────────────────────────────────

interface PeerSenderState {
  channel: RTCDataChannel;
  currentChunk: number;
  isPumping: boolean;
  priorityQueue: number[];
  sentChunks: Set<number>;
}

export class HostVideoSender {
  private file: File | null = null;
  private duration = 0;
  private streamId = '';
  private peers = new Map<string, PeerSenderState>();

  setMedia(file: File, duration: number, roomId: string): void {
    this.file = file;
    this.duration = duration;
    this.streamId = roomId;

    // Reset and notify any already connected peers of the new media
    this.peers.forEach((peerState, peerId) => {
      peerState.currentChunk = 0;
      peerState.priorityQueue = [];
      peerState.sentChunks.clear();
      this.initPeerTransfer(peerId, peerState);
    });
  }

  updateDuration(duration: number): void {
    if (duration > 0 && duration !== this.duration) {
      this.duration = duration;
      // Resend metadata if peers are connected
      this.peers.forEach((peerState, _peerId) => {
        if (peerState.channel.readyState === 'open' && this.file) {
          this.sendMetadata(peerState.channel);
        }
      });
    }
  }

  attachChannel(peerId: string, channel: RTCDataChannel): void {
    channel.binaryType = 'arraybuffer';
    channel.bufferedAmountLowThreshold = BUFFER_THRESHOLD;

    const peerState: PeerSenderState = {
      channel,
      currentChunk: 0,
      isPumping: false,
      priorityQueue: [],
      sentChunks: new Set(),
    };

    this.peers.set(peerId, peerState);

    channel.onopen = () => {
      console.log(`[VideoTransfer] DataChannel opened with guest: ${peerId}`);
      if (this.file) {
        this.initPeerTransfer(peerId, peerState);
      }
    };

    channel.onclose = () => {
      console.log(`[VideoTransfer] DataChannel closed with guest: ${peerId}`);
      this.peers.delete(peerId);
    };

    channel.onerror = (err) => {
      console.warn(`[VideoTransfer] DataChannel error with guest ${peerId}:`, err);
    };

    channel.onmessage = (event) => {
      if (typeof event.data === 'string') {
        try {
          const msg = JSON.parse(event.data) as StreamControlMessage;
          if (msg.type === 'SEEK_PRIORITY') {
            this.handleSeekPriority(peerId, msg.targetChunk);
          } else if (msg.type === 'CHUNK_REQUEST') {
            this.handleChunkRequest(peerId, msg.startChunk, msg.count);
          }
        } catch {}
      }
    };

    channel.onbufferedamountlow = () => {
      this.pump(peerId);
    };

    if (channel.readyState === 'open' && this.file) {
      this.initPeerTransfer(peerId, peerState);
    }
  }

  detachChannel(peerId: string): void {
    this.peers.delete(peerId);
  }

  notifySeek(currentTime: number): void {
    if (!this.file || this.duration <= 0) return;
    const totalChunks = Math.ceil(this.file.size / CHUNK_SIZE);
    const targetChunk = Math.max(0, Math.min(totalChunks - 1, Math.floor((currentTime / this.duration) * totalChunks)));

    this.peers.forEach((peerState, peerId) => {
      this.handleSeekPriority(peerId, targetChunk);
    });
  }

  private sendMetadata(channel: RTCDataChannel): void {
    if (!this.file) return;
    const totalChunks = Math.ceil(this.file.size / CHUNK_SIZE);
    const meta: FileMetadata = {
      type: 'FILE_META',
      fileName: this.file.name,
      fileSize: this.file.size,
      fileType: this.file.type || 'video/mp4',
      duration: this.duration,
      chunkSize: CHUNK_SIZE,
      totalChunks,
      streamId: this.streamId,
    };
    try {
      channel.send(JSON.stringify(meta));
    } catch (e) {
      console.warn('[VideoTransfer] Failed to send FILE_META:', e);
    }
  }

  private initPeerTransfer(peerId: string, peerState: PeerSenderState): void {
    if (!this.file) return;

    this.sendMetadata(peerState.channel);

    const totalChunks = Math.ceil(this.file.size / CHUNK_SIZE);

    // Queue Priority 1: Head chunks (first 2 MB) for initial container demuxing
    const headLimit = Math.min(HEAD_CHUNKS_COUNT, totalChunks);
    for (let i = 0; i < headLimit; i++) {
      if (!peerState.priorityQueue.includes(i)) {
        peerState.priorityQueue.push(i);
      }
    }

    // Queue Priority 2: Tail chunks (last 2 MB) in case moov atom is located at end of MP4
    const tailStart = Math.max(headLimit, totalChunks - TAIL_CHUNKS_COUNT);
    for (let i = tailStart; i < totalChunks; i++) {
      if (!peerState.priorityQueue.includes(i)) {
        peerState.priorityQueue.push(i);
      }
    }

    peerState.currentChunk = headLimit;
    this.pump(peerId);
  }

  private handleSeekPriority(peerId: string, targetChunk: number): void {
    const peerState = this.peers.get(peerId);
    if (!peerState || !this.file) return;

    const totalChunks = Math.ceil(this.file.size / CHUNK_SIZE);
    peerState.currentChunk = Math.max(0, targetChunk);

    // Prioritize next 32 chunks (2 MB) from targetChunk forward
    for (let i = Math.min(totalChunks - 1, targetChunk + 32); i >= targetChunk; i--) {
      const idx = peerState.priorityQueue.indexOf(i);
      if (idx !== -1) {
        peerState.priorityQueue.splice(idx, 1);
      }
      peerState.priorityQueue.unshift(i);
    }

    this.pump(peerId);
  }

  private handleChunkRequest(peerId: string, startChunk: number, count: number): void {
    const peerState = this.peers.get(peerId);
    if (!peerState || !this.file) return;

    const totalChunks = Math.ceil(this.file.size / CHUNK_SIZE);
    const endChunk = Math.min(totalChunks - 1, startChunk + count);

    for (let i = endChunk; i >= startChunk; i--) {
      const idx = peerState.priorityQueue.indexOf(i);
      if (idx !== -1) {
        peerState.priorityQueue.splice(idx, 1);
      }
      peerState.priorityQueue.unshift(i);
    }

    this.pump(peerId);
  }

  private async pump(peerId: string): Promise<void> {
    const peerState = this.peers.get(peerId);
    if (!peerState || !this.file || peerState.isPumping) return;
    const { channel } = peerState;
    if (channel.readyState !== 'open') return;

    peerState.isPumping = true;
    const totalChunks = Math.ceil(this.file.size / CHUNK_SIZE);

    try {
      while (channel.readyState === 'open' && channel.bufferedAmount < BUFFER_THRESHOLD) {
        let chunkIdx: number | null = null;

        // 1. Drain priority queue first
        if (peerState.priorityQueue.length > 0) {
          chunkIdx = peerState.priorityQueue.shift()!;
        } else if (peerState.currentChunk < totalChunks) {
          // 2. Sequential forward advance buffering
          chunkIdx = peerState.currentChunk++;
        } else {
          // All chunks sent to this peer
          break;
        }

        if (chunkIdx === null || chunkIdx >= totalChunks) continue;

        // Read chunk slice from local file
        const start = chunkIdx * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, this.file.size);
        const slice = this.file.slice(start, end);
        const arrayBuf = await slice.arrayBuffer();

        // 4-byte header: chunkIndex (Uint32 Little Endian) + binary data
        const packet = new Uint8Array(4 + arrayBuf.byteLength);
        new DataView(packet.buffer).setUint32(0, chunkIdx, true);
        packet.set(new Uint8Array(arrayBuf), 4);

        channel.send(packet.buffer);
        peerState.sentChunks.add(chunkIdx);
      }
    } catch (e) {
      console.warn(`[VideoTransfer] Pump error for peer ${peerId}:`, e);
    } finally {
      peerState.isPumping = false;
    }
  }
}

// ── Guest Video Receiver ──────────────────────────────────────

interface PendingRangeRequest {
  requestId: string;
  start: number;
  end: number;
  timer: ReturnType<typeof setTimeout>;
}

export class GuestVideoReceiver {
  private fileMeta: FileMetadata | null = null;
  private chunks = new Map<number, Uint8Array>();
  private activeChannel: RTCDataChannel | null = null;
  private virtualMediaChannel: BroadcastChannel | null = null;
  private pendingRequests = new Map<string, PendingRangeRequest>();
  private blobUrl: string | null = null;
  private streamId = '';
  private lastProgressUpdate = 0;

  constructor() {
    if (typeof BroadcastChannel !== 'undefined') {
      this.virtualMediaChannel = new BroadcastChannel('cinesync-virtual-media');
      this.virtualMediaChannel.onmessage = (event) => {
        this.handleServiceWorkerMessage(event.data);
      };
    }
  }

  attachChannel(channel: RTCDataChannel): void {
    this.activeChannel = channel;
    channel.binaryType = 'arraybuffer';

    channel.onopen = () => {
      console.log('[VideoTransfer] Guest DataChannel connected to host');
      usePlayerStore.getState().setBufferedProgress({ isStreamingBuffer: true });
    };

    channel.onclose = () => {
      console.log('[VideoTransfer] Guest DataChannel closed');
      usePlayerStore.getState().setBufferedProgress({ isStreamingBuffer: false });
    };

    channel.onmessage = (event) => {
      if (typeof event.data === 'string') {
        try {
          const msg = JSON.parse(event.data) as StreamControlMessage;
          if (msg.type === 'FILE_META') {
            this.handleFileMeta(msg);
          }
        } catch {}
      } else if (event.data instanceof ArrayBuffer) {
        this.handleBinaryChunk(event.data);
      }
    };
  }

  private handleFileMeta(meta: FileMetadata): void {
    console.log('[VideoTransfer] Received FILE_META:', meta.fileName, `${(meta.fileSize / 1024 / 1024).toFixed(1)} MB`);
    this.fileMeta = meta;
    this.streamId = meta.streamId || 'cinesync-room';

    if (meta.duration > 0) {
      usePlayerStore.getState().setDuration(meta.duration);
    }

    // Notify room of stream metadata so guest timeline and titles match host
    window.dispatchEvent(
      new CustomEvent('cinesync:stream-meta', {
        detail: { meta },
      })
    );
  }

  private handleBinaryChunk(data: ArrayBuffer): void {
    if (!this.fileMeta || data.byteLength < 4) return;

    const view = new DataView(data);
    const chunkIdx = view.getUint32(0, true);
    const chunkData = new Uint8Array(data, 4);

    this.chunks.set(chunkIdx, chunkData);

    // Check if any pending Service Worker range requests can now be fulfilled
    this.checkPendingRequests();

    const receivedCount = this.chunks.size;
    const totalChunks = this.fileMeta.totalChunks;
    const bufferedPercent = Math.min(100, Math.round((receivedCount / totalChunks) * 100));

    // Calculate YouTube-style forward buffer health in seconds
    const curTime = usePlayerStore.getState().currentTime || 0;
    const duration = this.fileMeta.duration || 1;
    const currentChunkIdx = Math.floor((curTime / duration) * totalChunks);

    let contiguousAhead = 0;
    while (this.chunks.has(currentChunkIdx + contiguousAhead) && currentChunkIdx + contiguousAhead < totalChunks) {
      contiguousAhead++;
    }

    const bytesPerSecond = this.fileMeta.fileSize / duration;
    const bufferHealthSeconds = Math.round((contiguousAhead * CHUNK_SIZE) / (bytesPerSecond || 1));
    const bufferedTime = Math.min(duration, curTime + bufferHealthSeconds);

    // Throttle progress updates to at most once per 250ms to keep React UI smooth
    const now = Date.now();
    if (now - this.lastProgressUpdate > 250 || receivedCount >= totalChunks) {
      this.lastProgressUpdate = now;
      usePlayerStore.getState().setBufferedProgress({
        bufferedPercent,
        bufferHealthSeconds,
        bufferedTime,
        isStreamingBuffer: true,
      });
    }

    // When 100% of chunks are in RAM, assemble native Blob URL for permanent instant hardware playback
    if (receivedCount >= totalChunks && !this.blobUrl) {
      try {
        const sortedParts: Uint8Array[] = [];
        for (let i = 0; i < totalChunks; i++) {
          const part = this.chunks.get(i);
          if (part) sortedParts.push(part);
        }
        const fullBlob = new Blob(sortedParts as any, { type: this.fileMeta.fileType || 'video/mp4' });
        this.blobUrl = URL.createObjectURL(fullBlob);
        console.log('[VideoTransfer] 100% movie buffered in RAM! Upgrading to native Blob URL');
        usePlayerStore.getState().setGuestStreamSrc(this.blobUrl);
        window.dispatchEvent(new CustomEvent('cinesync:stream-ready', { detail: { blobUrl: this.blobUrl } }));
      } catch (err) {
        console.warn('[VideoTransfer] Error creating full blob URL:', err);
      }
    }
  }

  private handleServiceWorkerMessage(data: any): void {
    if (!data || data.type !== 'GET_RANGE' || !this.fileMeta) return;

    const { requestId, rangeHeader } = data;
    const fileSize = this.fileMeta.fileSize;

    let start = 0;
    let end = Math.min(1024 * 1024 - 1, fileSize - 1); // 1 MB default initial slice

    if (rangeHeader) {
      const match = rangeHeader.match(/bytes=(\d+)-(\d*)/);
      if (match) {
        start = parseInt(match[1], 10);
        if (match[2]) {
          end = parseInt(match[2], 10);
        } else {
          // Open-ended range request: serve up to 1 MB at a time
          end = Math.min(start + 1024 * 1024 - 1, fileSize - 1);
        }
      }
    }

    if (start >= fileSize) {
      start = fileSize - 1;
      end = fileSize - 1;
    }
    end = Math.min(end, fileSize - 1);

    const startChunkIdx = Math.floor(start / CHUNK_SIZE);

    // If starting chunk is already in RAM, fulfill immediately
    if (this.chunks.has(startChunkIdx)) {
      this.respondRange(requestId, start, end);
      return;
    }

    // Starting chunk is NOT in RAM yet.
    // Request priority from host immediately! NEVER return zero-filled buffer!
    this.requestPriorityChunk(startChunkIdx);
    const endChunkIdx = Math.floor(end / CHUNK_SIZE);
    this.requestChunkRange(startChunkIdx, Math.min(32, endChunkIdx - startChunkIdx + 1));

    // Register pending request with 25s timeout
    const timer = setTimeout(() => {
      this.pendingRequests.delete(requestId);
      // If still missing after 25s, return error so SW can fail cleanly
      this.virtualMediaChannel?.postMessage({
        type: 'RANGE_RESPONSE',
        requestId,
        error: 'Range request timed out waiting for chunks over DataChannel',
      });
    }, 25000);

    this.pendingRequests.set(requestId, {
      requestId,
      start,
      end,
      timer,
    });
  }

  private checkPendingRequests(): void {
    if (this.pendingRequests.size === 0) return;

    const toFulfill: PendingRangeRequest[] = [];

    this.pendingRequests.forEach((req, id) => {
      const startChunkIdx = Math.floor(req.start / CHUNK_SIZE);
      if (this.chunks.has(startChunkIdx)) {
        toFulfill.push(req);
        this.pendingRequests.delete(id);
      }
    });

    for (const req of toFulfill) {
      clearTimeout(req.timer);
      this.respondRange(req.requestId, req.start, req.end);
    }
  }

  private respondRange(requestId: string, start: number, requestedEnd: number): void {
    if (!this.fileMeta) return;
    const fileSize = this.fileMeta.fileSize;

    // Find how many contiguous chunks are present from start
    let currentByte = start;
    let actualEnd = start;

    while (currentByte <= requestedEnd) {
      const chunkIdx = Math.floor(currentByte / CHUNK_SIZE);
      if (!this.chunks.has(chunkIdx)) {
        break; // Stop at first missing chunk
      }
      const chunkOffset = currentByte % CHUNK_SIZE;
      const bytesInChunk = Math.min(CHUNK_SIZE - chunkOffset, requestedEnd - currentByte + 1);
      actualEnd = currentByte + bytesInChunk - 1;
      currentByte += bytesInChunk;
    }

    // Ensure actualEnd is at least the end of the first chunk if available
    actualEnd = Math.max(start, Math.min(actualEnd, requestedEnd, fileSize - 1));

    // Slice REAL bytes (never zero-filled placeholder!)
    const length = actualEnd - start + 1;
    const result = new Uint8Array(length);
    let writeOffset = 0;
    let readByte = start;

    while (readByte <= actualEnd) {
      const chunkIdx = Math.floor(readByte / CHUNK_SIZE);
      const chunkOffset = readByte % CHUNK_SIZE;
      const chunk = this.chunks.get(chunkIdx);
      const bytesInChunk = Math.min(CHUNK_SIZE - chunkOffset, actualEnd - readByte + 1);

      if (chunk) {
        result.set(chunk.subarray(chunkOffset, chunkOffset + bytesInChunk), writeOffset);
      }

      writeOffset += bytesInChunk;
      readByte += bytesInChunk;
    }

    this.virtualMediaChannel?.postMessage({
      type: 'RANGE_RESPONSE',
      requestId,
      status: 206,
      headers: {
        'Content-Type': this.fileMeta.fileType || 'video/mp4',
        'Content-Range': `bytes ${start}-${actualEnd}/${fileSize}`,
        'Content-Length': `${length}`,
        'Accept-Ranges': 'bytes',
        'Cache-Control': 'no-cache',
      },
      data: result.buffer,
    });
  }

  requestPriorityChunk(chunkIdx: number): void {
    if (!this.activeChannel || this.activeChannel.readyState !== 'open' || !this.fileMeta) return;
    const curTime = (chunkIdx / this.fileMeta.totalChunks) * (this.fileMeta.duration || 1);
    try {
      this.activeChannel.send(
        JSON.stringify({
          type: 'SEEK_PRIORITY',
          targetChunk: chunkIdx,
          targetTime: curTime,
        } as StreamControlMessage)
      );
    } catch {}
  }

  requestChunkRange(startChunk: number, count: number): void {
    if (!this.activeChannel || this.activeChannel.readyState !== 'open') return;
    try {
      this.activeChannel.send(
        JSON.stringify({
          type: 'CHUNK_REQUEST',
          startChunk,
          count,
        } as StreamControlMessage)
      );
    } catch {}
  }

  requestSeek(targetTime: number): void {
    if (!this.activeChannel || this.activeChannel.readyState !== 'open' || !this.fileMeta) return;
    const totalChunks = this.fileMeta.totalChunks;
    const targetChunk = Math.max(0, Math.min(totalChunks - 1, Math.floor((targetTime / (this.fileMeta.duration || 1)) * totalChunks)));

    try {
      this.activeChannel.send(
        JSON.stringify({
          type: 'SEEK_PRIORITY',
          targetChunk,
          targetTime,
        } as StreamControlMessage)
      );
    } catch {}
  }

  reset(): void {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
    this.pendingRequests.forEach((req) => clearTimeout(req.timer));
    this.pendingRequests.clear();
    this.chunks.clear();
    this.fileMeta = null;
    this.activeChannel = null;
    this.lastProgressUpdate = 0;
    usePlayerStore.getState().setGuestStreamSrc(null);
    usePlayerStore.getState().setBufferedProgress({
      bufferedPercent: 0,
      bufferHealthSeconds: 0,
      bufferedTime: 0,
      isStreamingBuffer: false,
    });
  }
}

// Global Singleton instances
export const hostVideoSender = new HostVideoSender();
export const guestVideoReceiver = new GuestVideoReceiver();
